import { isBookable } from "@@/shared/acquire/is-bookable";
import type { Order } from "@@/shared/orders/order";
import type { OrderChangeItem } from "@@/shared/orders/order-changes/order-change-item";
import {
    orderAddItemFactory,
    orderItemChangeProductFactory,
    orderItemChangeTimeFactory,
    orderRemoveItemFactory,
} from "@@/shared/orders/order-changes/order-change-item";
import { isOrderItemBookable } from "@@/shared/orders/order-items/is-order-item";
import {
    isOrderItemAccommodation_V2,
    isOrderItemAccommodationRateItem_V2,
    isOrderItemBookableQuantity_V2,
} from "@@/shared/orders/order-items/is-order-item-v2";
import type { OrderItem } from "@@/shared/orders/order-items/order-item";
import { isOrderItemEqual } from "@@/shared/orders/order-items/order-item-comparer";
import type {
    OrderItemAccommodation_V2,
    OrderItemBookable_V2,
} from "@@/shared/orders/order-items/order-item-v2";
import { getChildren } from "@@/shared/orders/order-items/order-item-v2-helper";

/**
 * Calculates the differences between an existing order and a new order items (cart).
 * The function identifies items that are to be removed, added, or time-changed.
 * @param {Order} order - The existing order.
 * @param {newCartOrderItems} newCartOrderItems - The new order items from (cart).
 * @returns {OrderChanges} - An object containing changes required to transform the existing order into the new one.
 */
const calculateDeltaBetweenOrders_ACCOMMODATION = (
    order: Order,
    newCartOrderItems: OrderItem[],
    //cart: Cart,
) => {
    if (!newCartOrderItems.length) return; // If the cart is empty, return immediately.

    // Identify the bookable items in the old and new orders.
    const oldOrderItem = order.orderItems.find(isOrderItemAccommodation_V2);
    const newOrderItem = newCartOrderItems.find(isOrderItemAccommodation_V2);

    // Throw an error if no bookable item is found in the orders.
    if (!oldOrderItem) throw new Error("No oi found in order");
    if (!newOrderItem) throw new Error("No oi found in new order");

    // Check if the old and new bookable items are the same.
    const isSameAccommodation = isOrderItemEqual(oldOrderItem, newOrderItem);

    // Reuse the old parent bookable ID if the bookable item is the same.
    const newOrderItems = newCartOrderItems.map(oi =>
        oi.parentId !== newOrderItem._id || !isSameAccommodation
            ? oi
            : { ...oi, parentId: oldOrderItem._id },
    );

    // Check if the time is the same.
    const isSameTime = oldOrderItem.time._id === newOrderItem.time._id;

    // Identify changes that require removing items from the order.
    const removeChanges = calculateRemovals_ACCOMMODATION(
        order,
        newOrderItems,
        isSameAccommodation,
        isSameTime,
    );

    // Identify changes that require adding items to the order.
    const addChanges = calculateAdditions_ACCOMMODATION(
        order,
        newOrderItems,
        isSameAccommodation,
        oldOrderItem,
        isSameTime,
    );

    // Identify changes that require modifying the time.
    const timeChanges = !isSameTime
        ? [
              orderItemChangeTimeFactory(
                  oldOrderItem._id,
                  oldOrderItem.time,
                  newOrderItem.time,
              ),
          ]
        : [];
    return { changes: [...timeChanges, ...removeChanges, ...addChanges] };
};

/**
 * Calculates the differences between an existing order and a new order (cart).
 * The function identifies items that are to be removed, added, or time-changed.
 * @param {Order} order - The existing order.
 * @param {newCartOrderItems} newCartOrderItems - The new order (cart).
 * @returns {OrderChanges} - An object containing changes required to transform the existing order into the new one.
 */
const calculateDeltaBetweenOrders_BOOKABLE = (
    order: Order,
    newCartOrderItems: OrderItem[],
) => {
    if (!newCartOrderItems.length) return; // If the cart is empty, return immediately.

    // Identify the bookable items in the old and new orders.
    const oldBookable = order.orderItems.find(isOrderItemBookable);
    const newBookable = newCartOrderItems.find(isOrderItemBookable);

    // Throw an error if no bookable item is found in the orders.
    if (!oldBookable) throw new Error("No bookable found in order");
    if (!newBookable) throw new Error("No bookable found in new order");

    // Check if the old and new bookable items are the same.
    const isSameBookable = oldBookable.productId === newBookable.productId;

    // Reuse the old parent bookable ID if the bookable item is the same.
    const newOrderItems = newCartOrderItems.map(oi =>
        oi.parentId !== newBookable._id || !isSameBookable
            ? oi
            : { ...oi, parentId: oldBookable._id },
    );

    // Check if the time is the same.
    const isSameTime = oldBookable.time._id === newBookable.time._id;

    // Identify changes that require removing items from the order.
    const removeChanges = calculateRemovals_BOOKABLE(
        order,
        newOrderItems,
        isSameBookable,
        isSameTime,
    );

    // Identify changes that require adding items to the order.
    const addChanges = calculateAdditions_BOOKABLE(
        order,
        newOrderItems,
        isSameBookable,
        oldBookable,
        isSameTime,
    );

    // Identify changes that require modifying the time.
    const timeChanges = !isSameTime
        ? [
              orderItemChangeTimeFactory(
                  oldBookable._id,
                  oldBookable.time,
                  newBookable.time,
              ),
          ]
        : [];

    // is different bookable
    const productChanges = !isSameBookable
        ? [
              orderItemChangeProductFactory(
                  newBookable._id,
                  {
                      id: oldBookable.productId,
                      title: oldBookable.meta.title,
                  },
                  {
                      id: newBookable.productId,
                      title: newBookable.meta.title,
                  },
              ),
          ]
        : [];

    return {
        changes: [
            ...productChanges,
            ...timeChanges,
            ...removeChanges,
            ...addChanges,
        ],
    };
};

/**
 * Helper function that calculates the removals needed to transform the old order into the new order.
 * @param {Order} order - The existing order.
 * @param {OrderItem[]} newOrderItems - The new order items.
 * @param {boolean} isSameBookable - A flag indicating if the bookable item is the same in both orders.
 * @param {boolean} isSameTime - A flag indicating if the time is the same in both orders.
 * @returns {OrderChangeItem[]} - An array of changes required for removing items.
 */
const calculateRemovals_BOOKABLE = (
    order: Order,
    newOrderItems: OrderItem[],
    isSameBookable: boolean,
    isSameTime: boolean,
): OrderChangeItem[] => {
    return order.orderItems.flatMap(oi => {
        if (oi.acquireType === "DISCOUNT" || oi.parentId) return [];

        const oldChildren = getChildren(oi, order.orderItems);

        if (!isSameBookable && isBookable(oi)) {
            return [
                orderRemoveItemFactory(oi),
                ...oldChildren.map(orderRemoveItemFactory),
            ];
        }

        if (isSameBookable && isBookable(oi)) {
            const removables = oldChildren.flatMap(oldChild => {
                const exists = newOrderItems.some(oi2 =>
                    isOrderItemEqual(oldChild, oi2),
                );
                const allChildrenAreEqual = areOrderItemAndItsChildrenEqual(
                    oldChild,
                    order.orderItems,
                    newOrderItems,
                );

                if (exists && allChildrenAreEqual) return [];

                return [orderRemoveItemFactory(oldChild)];
            });

            return isSameTime ? removables : [...removables];
        }

        const allChildrenAreEqual = areOrderItemAndItsChildrenEqual(
            oi,
            order.orderItems,
            newOrderItems,
        );

        if (allChildrenAreEqual) return [];

        return [
            orderRemoveItemFactory(oi),
            ...oldChildren.map(orderRemoveItemFactory),
        ];
    });
};

/**
 * Helper function that calculates the additions needed to transform the old order into the new order.
 * @param {Order} order - The existing order.
 * @param {OrderItem[]} newOrderItems - The new order items.
 * @param {boolean} isSameBookable - A flag indicating if the bookable item is the same in both orders.
 * @param {OrderItem} oldBookable - The bookable item in the old order.
 * @param {boolean} isSameTime - A flag indicating if the time is the same in both orders.
 * @returns {OrderChangeItem[]} - An array of changes required for adding items.
 */
const calculateAdditions_BOOKABLE = (
    order: Order,
    newOrderItems: OrderItem[],
    isSameBookable: boolean,
    oldBookable: OrderItemBookable_V2,
    isSameTime: boolean,
): OrderChangeItem[] => {
    return newOrderItems.flatMap(oi => {
        if (oi.acquireType === "DISCOUNT" || !!oi.parentId) return [];

        if (!isSameBookable && isBookable(oi)) {
            return [
                orderAddItemFactory(oi),
                ...getChildren(oi, newOrderItems).map(orderAddItemFactory),
            ];
        }

        if (isSameBookable && isBookable(oi)) {
            const oldChildren = getChildren(oldBookable, order.orderItems);
            const newChildren = getChildren(oldBookable, newOrderItems);
            return newChildren.flatMap<OrderChangeItem>(newChild => {
                const exists = oldChildren.find(oi2 =>
                    isOrderItemEqual(newChild, oi2),
                );

                const allChildrenAreEqual = areOrderItemAndItsChildrenEqual(
                    newChild,
                    newOrderItems,
                    order.orderItems,
                );

                if (exists && allChildrenAreEqual) {
                    if (
                        !isSameTime &&
                        isOrderItemBookableQuantity_V2(exists) &&
                        isOrderItemBookableQuantity_V2(newChild)
                    ) {
                        return [
                            orderItemChangeTimeFactory(
                                exists._id,
                                exists.time,
                                newChild.time,
                            ),
                        ];
                    }
                    return [];
                }
                return [orderAddItemFactory(newChild)];
            });
        }

        const allChildrenAreEqual = areOrderItemAndItsChildrenEqual(
            oi,
            newOrderItems,
            order.orderItems,
        );

        if (allChildrenAreEqual) {
            return [];
        }

        const newChildren = getChildren(oi, newOrderItems);

        return [
            orderAddItemFactory(oi),
            ...newChildren.map(orderAddItemFactory),
        ];
    });
};

/**
 * Helper function that calculates the additions needed to transform the old order into the new order.
 * @param {Order} order - The existing order.
 * @param {OrderItem[]} newOrderItems - The new order items.
 * @param {boolean} isSameBookable - A flag indicating if the bookable item is the same in both orders.
 * @param {OrderItem} oldBookable - The bookable item in the old order.
 * @param {boolean} isSameTime - A flag indicating if the time is the same in both orders.
 * @returns {OrderChangeItem[]} - An array of changes required for adding items.
 */
const calculateAdditions_ACCOMMODATION = (
    order: Order,
    newOrderItems: OrderItem[],
    isSameParent: boolean,
    oldBookable: OrderItemAccommodation_V2,
    isSameTime: boolean,
): OrderChangeItem[] => {
    return newOrderItems.flatMap(oi => {
        if (oi.acquireType === "DISCOUNT" || !!oi.parentId) return [];

        if (!isSameParent && isOrderItemAccommodation_V2(oi)) {
            return [
                orderAddItemFactory(oi),
                ...getChildren(oi, newOrderItems).map(orderAddItemFactory),
            ];
        }

        if (isSameParent && isOrderItemAccommodation_V2(oi)) {
            const oldChildren = getChildren(oldBookable, order.orderItems);
            const newChildren = getChildren(oldBookable, newOrderItems);
            return newChildren.flatMap<OrderChangeItem>(newChild => {
                const exists = oldChildren.find(oi2 =>
                    isOrderItemEqual(newChild, oi2),
                );

                const allChildrenAreEqual = areOrderItemAndItsChildrenEqual(
                    newChild,
                    newOrderItems,
                    order.orderItems,
                );

                if (exists && allChildrenAreEqual) {
                    if (
                        !isSameTime &&
                        isOrderItemAccommodationRateItem_V2(exists) &&
                        isOrderItemAccommodationRateItem_V2(newChild)
                    ) {
                        return [
                            orderItemChangeTimeFactory(
                                exists._id,
                                exists.time,
                                newChild.time,
                            ),
                        ];
                    }
                    return [];
                }
                return [orderAddItemFactory(newChild)];
            });
        }

        const allChildrenAreEqual = areOrderItemAndItsChildrenEqual(
            oi,
            newOrderItems,
            order.orderItems,
        );

        if (allChildrenAreEqual) {
            return [];
        }

        const newChildren = getChildren(oi, newOrderItems);

        return [
            orderAddItemFactory(oi),
            ...newChildren.map(orderAddItemFactory),
        ];
    });
};

/**
 * Helper function that calculates the removals needed to transform the old order into the new order.
 * @param {Order} order - The existing order.
 * @param {OrderItem[]} newOrderItems - The new order items.
 * @param {boolean} isSameBookable - A flag indicating if the bookable item is the same in both orders.
 * @param {boolean} isSameTime - A flag indicating if the time is the same in both orders.
 * @returns {OrderChangeItem[]} - An array of changes required for removing items.
 */
const calculateRemovals_ACCOMMODATION = (
    order: Order,
    newOrderItems: OrderItem[],
    isSameBookable: boolean,
    isSameTime: boolean,
): OrderChangeItem[] => {
    return order.orderItems.flatMap(oi => {
        if (oi.acquireType === "DISCOUNT" || oi.parentId) return [];

        const oldChildren = getChildren(oi, order.orderItems);

        if (!isSameBookable && isOrderItemAccommodation_V2(oi)) {
            return [
                orderRemoveItemFactory(oi),
                ...oldChildren.map(orderRemoveItemFactory),
            ];
        }

        if (isSameBookable && isOrderItemAccommodation_V2(oi)) {
            const removables = oldChildren.flatMap(oldChild => {
                const exists = newOrderItems.some(oi2 =>
                    isOrderItemEqual(oldChild, oi2),
                );
                const allChildrenAreEqual = areOrderItemAndItsChildrenEqual(
                    oldChild,
                    order.orderItems,
                    newOrderItems,
                );

                if (exists && allChildrenAreEqual) return [];

                return [orderRemoveItemFactory(oldChild)];
            });

            return isSameTime ? removables : [...removables];
        }

        const allChildrenAreEqual = areOrderItemAndItsChildrenEqual(
            oi,
            order.orderItems,
            newOrderItems,
        );

        if (allChildrenAreEqual) return [];

        return [
            orderRemoveItemFactory(oi),
            ...oldChildren.map(orderRemoveItemFactory),
        ];
    });
};

const areChildrenEqual = (
    parentItem1: OrderItem,
    parentItem2: OrderItem,
    childrenOfParent1: OrderItem[],
    childrenOfParent2: OrderItem[],
): boolean => {
    const childrenOfItem1 = getChildren(parentItem1, childrenOfParent1);
    const childrenOfItem2 = getChildren(parentItem2, childrenOfParent2);
    // if(isOrderItemBuyable_V2(parentItem1))
    //     browserLogger.info({childrenOfItem1,childrenOfItem2})
    if (childrenOfItem1.length !== childrenOfItem2.length) return false;

    return childrenOfItem1.every(childOfItem1 =>
        childrenOfItem2.some(childOfItem2 =>
            isOrderItemEqual(childOfItem1, childOfItem2),
        ),
    );
};

const areOrderItemAndItsChildrenEqual = (
    orderItem1: OrderItem,
    childrenOfOrderItem1: OrderItem[],
    childrenOfOrderItem2: OrderItem[],
): boolean => {
    const correspondingOrderItem2 = childrenOfOrderItem2.find(child =>
        isOrderItemEqual(orderItem1, child),
    );
    if (!correspondingOrderItem2) {
        return false;
    }
    return areChildrenEqual(
        orderItem1,
        correspondingOrderItem2,
        childrenOfOrderItem1,
        childrenOfOrderItem2,
    );
};

export {
    calculateDeltaBetweenOrders_ACCOMMODATION,
    calculateDeltaBetweenOrders_BOOKABLE,
};
